import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ApiError } from '@/lib/api';
import { directoryApi, type DirectoryConnection, type DirectoryPerson } from '@/lib/directory';
import {
  Button,
  Card,
  CONTROL,
  FilterField,
  Initials,
  LoadingLine,
  Notice,
  PageHeading,
  Refreshing,
  SkeletonRows,
  StatusChip,
  type StatusTone,
} from '@/components/ui';

/**
 * Screen 7 — Directory.
 *
 * THE PEOPLE IN THE CONNECTED CALENDAR, not the people in ZenSpace. This screen
 * used to list ZenCore's organization users, which answered a different
 * question: a ZenSpace organization is usually a handful of administrators,
 * while the question asked in front of a calendar connection is "who in this
 * company can put a meeting in these rooms?". That list lives in the customer's
 * own Workspace or tenant and is read live from it.
 *
 * Read-only by contract, all the way down: this service never creates, renames
 * or removes anything in a customer's directory.
 *
 * PAGING IS A CURSOR STACK rather than page numbers. Both providers hand back a
 * cursor and neither will skip ahead, so "Previous" is the cursors we have
 * already used — an honest Back, not an invented page number.
 */
export function Directory() {
  const [people, setPeople] = useState<DirectoryPerson[] | null>(null);
  const [connection, setConnection] = useState<DirectoryConnection | null>(null);
  const [sources, setSources] = useState<DirectoryConnection[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [needsReconsent, setNeedsReconsent] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const [search, setSearch] = useState('');
  const [appliedSearch, setAppliedSearch] = useState('');
  /** Empty means "whichever the adapter picks", which is the first connection. */
  const [sourceId, setSourceId] = useState('');
  const [status, setStatus] = useState<'any' | 'active'>('any');
  /** Where we are. The last entry is this page; `[]` is the first page. */
  const [trail, setTrail] = useState<string[]>([]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setAppliedSearch(search);
      // A new search starts at the beginning. A cursor from the previous query
      // continues a list that no longer exists.
      //
      // `t.length ? [] : t` rather than `[]`: a plain `[]` is a NEW array every
      // time, which changes `load`, which re-runs the fetch — so every mount
      // and every keystroke that did not actually move us off page one paid for
      // a second read of somebody's directory.
      setTrail((t) => (t.length ? [] : t));
    }, 250);
    return () => clearTimeout(timer);
  }, [search]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const result = await directoryApi.people({
        limit: 25,
        search: appliedSearch,
        status,
        connectionId: sourceId || undefined,
        cursor: trail[trail.length - 1] ?? null,
      });
      setPeople(result.people);
      setConnection(result.connection);
      setSources(result.connections);
      setNextCursor(result.next_cursor);
      setNeedsReconsent(result.needs_reconsent);
      setMessage(result.message);
      setError(null);
    } catch (err) {
      setError(
        err instanceof ApiError ? err.message : 'We could not read your calendar directory.',
      );
    } finally {
      setLoading(false);
    }
  }, [appliedSearch, trail, status, sourceId]);

  useEffect(() => {
    void load();
  }, [load]);

  const firstRead = people === null && loading;
  const notConnected = people !== null && connection === null;
  /* Searching gives up paging on Google — two queries, two cursors, no merge. */
  const searching = appliedSearch.length > 0;

  return (
    <div className="mx-auto w-full max-w-[1100px] px-[28px] py-[40px]">
      <div className="mb-[28px]">
        <PageHeading
          eyebrow="Your calendar"
          title="Directory"
          sub={
            connection
              ? `Everyone in ${connection.domain ?? connection.provider_name}, read live from ` +
                `${connection.provider_name}. Read-only here — accounts are managed where they live.`
              : 'The people in your own Google Workspace or Microsoft 365 directory.'
          }
        />
      </div>

      {error && (
        <div className="mb-[20px]">
          <Notice tone="broken" title={error} />
        </div>
      )}

      {firstRead ? (
        <SkeletonRows rows={5} label="Reading your directory…" />
      ) : notConnected ? (
        <Notice tone="attention" title={message}>
          <Link to="/connections" className="font-semibold text-brand underline">
            Connect a calendar
          </Link>{' '}
          and the people in it appear here.
        </Notice>
      ) : needsReconsent ? (
        /* The grant is the problem, not the person asking. Reading the
           directory needs a permission that is newer than most connections, so
           a calendar that syncs perfectly can still refuse exactly here. */
        <Notice tone="attention" title={message}>
          <Link to="/connections" className="font-semibold text-brand underline">
            Re-connect {connection?.provider_name ?? 'your calendar'}
          </Link>{' '}
          to grant it. Nothing about your rooms, mappings or bookings is affected either way.
        </Notice>
      ) : (
        <>
          <Card className="mb-[24px]">
            <div className="flex flex-wrap items-end gap-[14px]">
              <FilterField label="Search people" grow>
                <input
                  type="search"
                  value={search}
                  placeholder="Name or email address"
                  onChange={(e) => setSearch(e.target.value)}
                  className={CONTROL}
                />
              </FilterField>

              {/* Only when there is a choice to make. An organization with one
                  calendar does not need to be told which one it is — the
                  subtitle already says. */}
              {sources.length > 1 && (
                <FilterField label="Calendar">
                  <select
                    value={sourceId || (connection?.id ?? '')}
                    onChange={(e) => {
                      setSourceId(e.target.value);
                      // A cursor belongs to one provider. Carrying it across
                      // would ask Microsoft to continue a Google page.
                      setTrail([]);
                    }}
                    className={CONTROL}
                  >
                    {sources.map((source) => (
                      <option key={source.id} value={source.id}>
                        {source.domain ?? source.provider_name}
                      </option>
                    ))}
                  </select>
                </FilterField>
              )}

              <FilterField label="Account">
                <select
                  value={status}
                  onChange={(e) => {
                    setStatus(e.target.value === 'active' ? 'active' : 'any');
                    setTrail([]);
                  }}
                  className={CONTROL}
                >
                  <option value="any">Everyone</option>
                  <option value="active">Active only</option>
                </select>
              </FilterField>

              <div className="ml-auto flex items-center gap-[14px] text-sub text-muted">
                {loading ? (
                  <LoadingLine>Updating…</LoadingLine>
                ) : (
                  <span>
                    {/* A count, never a total: a cursor list does not know one,
                        and a made-up total is worse than none. */}
                    {people?.length ?? 0} {people?.length === 1 ? 'person' : 'people'}
                    {searching ? ' matching' : nextCursor ? ' on this page' : ''}
                  </span>
                )}
              </div>
            </div>
          </Card>

          {people !== null && people.length === 0 ? (
            <Notice tone="neutral" title={message} />
          ) : (
            <Refreshing busy={loading}>
              <Card padded={false} className="overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full border-collapse text-body">
                    <caption className="zs-sr-only">
                      People in the connected calendar directory, with their role and account
                      status.
                    </caption>
                    <thead>
                      <tr className="border-b border-divider bg-page/60 text-left">
                        <Th>Person</Th>
                        <Th>Job title</Th>
                        <Th>Department</Th>
                        <Th align="right">Account</Th>
                      </tr>
                    </thead>
                    <tbody>
                      {(people ?? []).map((person) => (
                        <PersonRow key={person.externalId} person={person} />
                      ))}
                    </tbody>
                  </table>
                </div>
              </Card>
            </Refreshing>
          )}

          {(trail.length > 0 || nextCursor) && (
            <div className="mt-[20px] flex items-center justify-between gap-[14px]">
              <span className="text-sub text-muted">
                {searching
                  ? 'Showing the closest matches. Narrow the search to see fewer.'
                  : `Page ${trail.length + 1}`}
              </span>
              <div className="flex gap-[10px]">
                <Button
                  tone="quiet"
                  disabled={trail.length === 0 || loading}
                  onClick={() => setTrail((t) => t.slice(0, -1))}
                >
                  Previous
                </Button>
                <Button
                  tone="quiet"
                  disabled={!nextCursor || loading}
                  onClick={() => setTrail((t) => (nextCursor ? [...t, nextCursor] : t))}
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function PersonRow({ person }: { person: DirectoryPerson }) {
  return (
    <tr className="border-b border-divider transition-colors last:border-0 hover:bg-page">
      <td className="px-[18px] py-[14px] align-top">
        <div className="flex items-center gap-[12px]">
          {/* Initials rather than a fetched photo: a per-row image is 25 more
              calls against the provider's quota for decoration. */}
          <Initials name={person.name ?? person.email ?? '?'} size={32} />
          <div>
            <span className="block font-medium text-ink">
              {person.name ?? 'No name given'}
              {person.isAdmin && (
                <span className="ml-[8px] align-middle text-micro font-semibold text-muted">
                  ADMIN
                </span>
              )}
            </span>
            <span className="block text-caption text-muted">
              {person.email ?? 'No email address'}
            </span>
          </div>
        </div>
      </td>

      <td className="px-[18px] py-[14px] align-top text-muted">{person.jobTitle ?? '—'}</td>

      <td className="px-[18px] py-[14px] align-top text-muted">
        {person.department ?? person.officeLocation ?? '—'}
      </td>

      <td className="px-[18px] py-[14px] align-top text-right whitespace-nowrap">
        <StatusChip tone={statusTone(person.status)}>{STATUS_LABEL[person.status]}</StatusChip>
      </td>
    </tr>
  );
}

/**
 * Suspended is amber rather than red: it is a deliberate administrative state,
 * not a fault. Archived is grey — the account is gone but its meetings are not,
 * which is exactly why the row is still worth showing.
 */
function statusTone(status: DirectoryPerson['status']): StatusTone {
  if (status === 'active') return 'healthy';
  if (status === 'suspended') return 'attention';
  return 'neutral';
}

const STATUS_LABEL: Record<DirectoryPerson['status'], string> = {
  active: 'Active',
  suspended: 'Suspended',
  archived: 'Archived',
};

function Th({ children, align = 'left' }: { children: React.ReactNode; align?: 'left' | 'right' }) {
  return (
    <th
      className={
        align === 'right'
          ? 'zs-eyebrow px-[18px] py-[12px] text-right font-semibold text-muted'
          : 'zs-eyebrow px-[18px] py-[12px] font-semibold text-muted'
      }
    >
      {children}
    </th>
  );
}
